import React, { useCallback } from "react";
import { useNavigate, generatePath, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { XLg } from "react-bootstrap-icons";
import { Button, Container, Offcanvas } from "react-bootstrap";
import LanguageSelectWrapper from "@/components/languageSelectWrapper";
import { SIDEBAR_NAV_KEYS } from "@/constants/navigationKeys";
import { navItemsConfig } from "@/components/navigation/config/navItemsConfig";
import MenuBadgeItem from "@/components/ui/menuBadgeItem";
import { buildPathWithLocale } from "@/utils/languageVersions/localeHelpers/pathWithLocale";
import BranchInfoHeader from "@/components/ui/branchInfoHeader";
import type { TNavItemConfig } from "@/types";
import { useCartSummary } from "@/components/cart/hooks/useCartSummary";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";
import { useGetMenuIdQuery } from "@/graphql/menuId/menuId.hooks";

interface IMobileSidebarProps {
  show: boolean;
  onHide: () => void;
  onOpenSelected: () => void;
}

const Sidebar: React.FC<IMobileSidebarProps> = ({
  show,
  onHide,
  onOpenSelected,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale, menuId, branchId, typeMenu } = useParams<{
    locale?: string;
    menuId?: string;
    branchId?: string;
    typeMenu?: string;
  }>();
  const menuTypeEnum = getMenuTypeFromUrl(typeMenu);
  const { data: menuIdData } = useGetMenuIdQuery({
    skip: !branchId || !menuTypeEnum,
    variables: {
      branchId: Number(branchId),
      typeMenu: menuTypeEnum,
    },
  });

  const menuData = menuId ? menuId : menuIdData?.menu?.id;
  const { totalQuantity } = useCartSummary(Number(menuData));

  const buildItemClickHandler = useCallback(
    (item: TNavItemConfig) => () => {
      if (item.type === "action") {
        if (item.actionId === "selected") {
          onOpenSelected();
        }
        return;
      }

      if (item.type !== "link" || !item.path) return;

      const resolvedPath = (() => {
        if (!branchId || !typeMenu) return item.path;

        if (menuId && item.path) {
          return generatePath(item.path, {
            menuId,
            branchId,
            typeMenu,
          });
        }

        if (!menuId && item.activePath) {
          return generatePath(item.activePath, {
            branchId,
            typeMenu,
          });
        }

        return item.path;
      })();

      navigate(buildPathWithLocale(locale ?? "", resolvedPath));
      onHide();
    },
    [locale, menuId, branchId, typeMenu, navigate, onHide, onOpenSelected]
  );

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="start"
      className="d-md-none mobileSidebar"
      backdrop={false}
      scroll
    >
      <Offcanvas.Header className="d-none" />

      <Offcanvas.Body className="p-0 d-flex flex-column h-100 bg-white">
        <div>
          <Container
            fluid
            className="d-flex align-items-center justify-content-between"
            style={{ height: "var(--app-header-height-mobile)" }}
          >
            <LanguageSelectWrapper view="mobile" />
            <Button
              variant="link"
              type="button"
              className="btn btn-link p-0 text-dark"
              onClick={onHide}
              aria-label="Close sidebar"
            >
              <XLg
                className="lh-1 text-text-primary fs-32"
                aria-hidden="true"
                focusable="false"
              />
            </Button>
          </Container>
        </div>
        <div className="flex-grow-1 overflow-auto p-3">
          <BranchInfoHeader classes="justify-content-center mb-5" />
          <div className="d-flex flex-column gap-2">
            {SIDEBAR_NAV_KEYS.map((navKey) => {
              const item = navItemsConfig[navKey];
              return (
                <MenuBadgeItem
                  key={item.key}
                  name={t(item.label)}
                  iconKey={item.iconKey}
                  onClick={buildItemClickHandler(item)}
                  selectedCount={
                    item.key === "selected" ? totalQuantity : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default Sidebar;
